/**
 * PDP Media Gallery (AC-141) — vendored copy of assets/drag-zoom-wrapper.js (2026-07-10).
 * defines <pdp-drag-zoom-wrapper>.
 * Namespaced for the pdp-media-gallery block; modify THIS copy, never the original.
 * External touchpoints: see PDPGallery-Implementation-2026-07-10.md on the drive.
 */
import { DialogCloseEvent } from '@theme/dialog';
import { clamp, preventDefault, isMobileBreakpoint, prefersReducedMotion } from '@theme/utilities';
import { Component } from '@theme/component';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const DEFAULT_ZOOM = 1.5;
/**
 * AC-141 Figs zoom mode (2026-07-11): measured Swiper zoom config on the Figs
 * dialog is maxRatio 2 / minRatio 1 / toggle:true (FIGS-RUNTIME-ADDENDUM §6).
 * Used only when the wrapper carries data-zoom-mode="figs".
 */
const FIGS_MAX_ZOOM = 2;
const DOUBLE_TAP_DELAY = 300;
const DOUBLE_TAP_DISTANCE = 50;
const DRAG_THRESHOLD = 10;

/**
 * @typedef {object} Refs
 * @property {HTMLImageElement} image - The image element to zoom and drag.
 */

/** @extends {Component<Refs>} */
export class DragZoomWrapper extends Component {
  requiredRefs = ['image'];
  #controller = new AbortController();
  /** @type {number} */
  #scale = DEFAULT_ZOOM;
  /** @type {number} */
  #initialDistance = 0;
  /** @type {number} */
  #startScale = DEFAULT_ZOOM;
  /** @type {Point} */
  #translate = { x: 0, y: 0 };
  /** @type {Point} */
  #startPosition = { x: 0, y: 0 };
  /** @type {Point} */
  #startTranslate = { x: 0, y: 0 };
  /** @type {boolean} */
  #isDragging = false;
  /** @type {boolean} */
  #initialized = false;
  /** @type {number | null} */
  #animationFrame = null;
  /** @type {number} */
  #lastTapTime = 0;
  /** @type {Point | null} */
  #lastTapPosition = null;

  /** @type {boolean} */
  #hasDraggedBeyondThreshold = false;

  /** @type {boolean} */
  #hasManualZoom = false;

  /**
   * AC-141 figs mode: swallow the click generated at the end of a mouse drag
   * so it cannot reach the dialog's backdrop-close handler.
   * @type {boolean}
   */
  #suppressNextClick = false;

  get #image() {
    return this.refs.image;
  }

  /**
   * AC-141 Figs zoom mode (data-zoom-mode="figs"): 1x–2x zoom, double-click /
   * double-tap toggle, mouse-drag panning, native swipe at 1x. When false,
   * behavior is exactly the vendored Horizon default.
   */
  get #isFigs() {
    return this.dataset.zoomMode === 'figs';
  }

  /** Upper zoom bound (Figs caps at 2x, Horizon default at 5x). */
  get #maxZoom() {
    return this.#isFigs ? FIGS_MAX_ZOOM : MAX_ZOOM;
  }

  /** Target scale of the double-tap / double-click toggle. */
  get #toggleZoom() {
    return this.#isFigs ? FIGS_MAX_ZOOM : DEFAULT_ZOOM;
  }

  /** Scale the wrapper rests at (and resets to when the dialog closes). */
  get #restZoom() {
    return this.#isFigs ? MIN_ZOOM : DEFAULT_ZOOM;
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.#image) return;

    this.#initResizeListener();
    window.addEventListener(DialogCloseEvent.eventName, this.#resetZoom);

    if (this.#isFigs) {
      // Figs mode is active on every breakpoint: touch pinch/drag/double-tap
      // plus the mouse double-click toggle and drag panning.
      this.#scale = MIN_ZOOM;
      this.#startScale = MIN_ZOOM;
      this.#initEventListeners();
      this.#initFigsPointerListeners();
      return;
    }

    if (!isMobileBreakpoint()) return;

    this.#initEventListeners();
    this.#updateTransform();
  }

  #initResizeListener() {
    this.#resizeObserver.observe(this);
  }

  #initEventListeners() {
    if (this.#initialized) return;
    this.#initialized = true;
    const { signal } = this.#controller;
    const options = { passive: false, signal };

    this.addEventListener('touchstart', this.#handleTouchStart, options);
    this.addEventListener('touchmove', this.#handleTouchMove, options);
    this.addEventListener('touchend', this.#handleTouchEnd, options);

    // Initialize transform immediately
    this.#updateTransform();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(DialogCloseEvent.eventName, this.#resetZoom);
    this.#controller.abort();
    this.#resizeObserver.disconnect();
    this.#cancelAnimationFrame();
  }

  #handleResize = () => {
    if (!this.#initialized && isMobileBreakpoint()) {
      this.#initEventListeners();
    }

    if (this.#initialized) {
      this.#requestUpdateTransform();
    }
  };

  #resizeObserver = new ResizeObserver(this.#handleResize);

  /**
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    if (this.#isFigs) return this.#handleFigsTouchStart(event);

    preventDefault(event);

    const touchCount = event.touches.length;

    if (touchCount === 2) {
      // Early exit if touches are invalid
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (!touch1 || !touch2) return;

      // Avoid object allocation by passing touches directly
      this.#startZoomGestureFromTouches(touch1, touch2);
    } else if (touchCount === 1) {
      const touch = event.touches[0];
      if (!touch) return;

      // Use performance.now() for better precision and performance
      const currentTime = performance.now();
      const timeSinceLastTap = currentTime - this.#lastTapTime;

      // Early exit if too much time has passed
      if (timeSinceLastTap >= DOUBLE_TAP_DELAY) {
        this.#storeTapInfo(currentTime, touch);
        this.#startDragGestureFromTouch(touch);
        return;
      }

      // Only check distance if we have a previous tap within time window
      if (this.#lastTapPosition) {
        // Distance calculation with early exit
        const distance = getDistance(touch, this.#lastTapPosition);

        if (distance < DOUBLE_TAP_DISTANCE) {
          // This is a double-tap, handle zoom toggle
          this.#handleDoubleTapFromTouch(touch);
          this.#lastTapTime = 0; // Reset to prevent triple-tap
          this.#lastTapPosition = null;
          return;
        }
      }

      // Store tap info for potential double-tap detection
      this.#storeTapInfo(currentTime, touch);
      this.#startDragGestureFromTouch(touch);
    }
  };

  /**
   * Start a zoom gesture with two touches
   * @param {Touch} touch1
   * @param {Touch} touch2
   */
  #startZoomGestureFromTouches(touch1, touch2) {
    // Calculate initial distance between touches
    this.#initialDistance = getDistance(touch1, touch2);
    this.#startScale = this.#scale;
    this.#isDragging = false;
  }

  /**
   * Start a drag gesture with a single touch
   * @param {Touch} touch
   */
  #startDragGestureFromTouch(touch) {
    this.#startPosition = { x: touch.clientX, y: touch.clientY };
    this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };
    this.#isDragging = true;
    this.#hasDraggedBeyondThreshold = false;
  }

  /**
   * Store tap information for double-tap detection
   * @param {number} currentTime
   * @param {Touch} touch
   */
  #storeTapInfo(currentTime, touch) {
    this.#lastTapTime = currentTime;
    this.#lastTapPosition = { x: touch.clientX, y: touch.clientY };
  }

  /**
   * Handle double-tap zoom toggle from touch
   * @param {Touch} touch - The touch where the double-tap occurred
   */
  #handleDoubleTapFromTouch(touch) {
    const containerCenter = {
      x: this.clientWidth / 2,
      y: this.clientHeight / 2,
    };

    let targetZoom;

    // If manual zoom has been used, reset to 1x
    if (this.#hasManualZoom) {
      targetZoom = MIN_ZOOM; // 1x
      this.#hasManualZoom = false; // Reset the flag
      this.#translate = { x: 0, y: 0 }; // Center the image
    } else {
      // Toggle between zoom levels: 1x ↔ the toggle target
      // (1.5x default / 2x in figs mode, AC-141)
      const tolerance = 0.05; // Small tolerance for floating point comparison

      if (Math.abs(this.#scale - MIN_ZOOM) < tolerance) {
        // Currently at 1x, go to the toggle target
        targetZoom = this.#toggleZoom;
      } else {
        // Currently at the toggle target or any other level, go to 1x
        targetZoom = MIN_ZOOM;
      }
    }

    // If we're not going to 1x, adjust translation to center zoom on the tap point
    if (targetZoom !== MIN_ZOOM) {
      const oldScale = this.#scale;
      this.#scale = Math.min(this.#maxZoom, targetZoom);

      // Calculate the distance from tap point to container center
      const distanceFromCenter = {
        x: touch.clientX - containerCenter.x,
        y: touch.clientY - containerCenter.y,
      };

      // Adjust translation to center zoom on the tap point
      const scaleDelta = this.#scale / oldScale - 1.0;
      this.#translate.x -= (distanceFromCenter.x * scaleDelta) / this.#scale;
      this.#translate.y -= (distanceFromCenter.y * scaleDelta) / this.#scale;
    } else {
      // Going to 1x, set the scale and center the image
      this.#scale = targetZoom;
      this.#translate = { x: 0, y: 0 }; // Center the image when going to 1x
    }

    this.#requestUpdateTransform();
  }

  /**
   * AC-141 Figs zoom mode touchstart: two-finger pinches and zoomed-in drags
   * are handled (and default-prevented) here; a single touch at 1x stays
   * native so the dialog's horizontal scroll-snap swipe navigates between
   * media. Double-taps toggle 1x/2x, matching Swiper's toggle:true.
   * @param {TouchEvent} event
   */
  #handleFigsTouchStart = (event) => {
    const touchCount = event.touches.length;

    if (touchCount === 2) {
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (!touch1 || !touch2) return;

      preventDefault(event);
      this.#startZoomGestureFromTouches(touch1, touch2);
      return;
    }

    if (touchCount !== 1) return;
    const touch = event.touches[0];
    if (!touch) return;

    const currentTime = performance.now();
    const timeSinceLastTap = currentTime - this.#lastTapTime;

    if (timeSinceLastTap < DOUBLE_TAP_DELAY && this.#lastTapPosition) {
      const distance = getDistance(touch, this.#lastTapPosition);

      if (distance < DOUBLE_TAP_DISTANCE) {
        preventDefault(event);
        this.#animateNextTransform();
        this.#handleDoubleTapFromTouch(touch);
        this.#lastTapTime = 0; // Reset to prevent triple-tap
        this.#lastTapPosition = null;
        return;
      }
    }

    this.#storeTapInfo(currentTime, touch);

    // Drag panning only applies while zoomed in; at 1x the touch stays native
    // (touch-action: pan-x in CSS) so the swipe navigates.
    if (this.#scale > MIN_ZOOM) {
      preventDefault(event);
      this.#startDragGestureFromTouch(touch);
    }
  };

  /**
   * @param {TouchEvent} event
   */
  #handleTouchMove = (event) => {
    if (this.#isFigs) return this.#handleFigsTouchMove(event);

    preventDefault(event);

    const touchCount = event.touches.length;

    if (touchCount === 2) {
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (touch1 && touch2) {
        this.#processZoomGesture(touch1, touch2);
      }
    } else if (touchCount === 1 && this.#isDragging) {
      const touch = event.touches[0];
      if (touch) {
        this.#processDragGesture(touch);
      }
    }
  };

  /**
   * Process zoom gesture from touches
   * @param {Touch} touch1
   * @param {Touch} touch2
   */
  #processZoomGesture(touch1, touch2) {
    // Calculate midpoint directly without object allocation
    const midX = (touch1.clientX + touch2.clientX) / 2;
    const midY = (touch1.clientY + touch2.clientY) / 2;

    // Calculate current distance between touches
    const currentDistance = getDistance(touch1, touch2);

    const oldScale = this.#scale;

    // Calculate and apply new scale
    const newScale = (currentDistance / this.#initialDistance) * this.#startScale;
    this.#scale = clamp(newScale, MIN_ZOOM, this.#maxZoom);

    // Mark that manual zoom has been used
    this.#hasManualZoom = true;

    // Adjust translation to keep the pinch midpoint stationary
    const containerCenterX = this.clientWidth / 2;
    const containerCenterY = this.clientHeight / 2;

    const distanceFromCenterX = midX - containerCenterX;
    const distanceFromCenterY = midY - containerCenterY;

    // Calculate how the image position needs to change to keep the midpoint stationary
    const scaleDelta = this.#scale / oldScale - 1.0;

    // Apply correction to prevent zooming on the opposite side of the midpoint
    this.#translate.x -= (distanceFromCenterX * scaleDelta) / this.#scale;
    this.#translate.y -= (distanceFromCenterY * scaleDelta) / this.#scale;

    this.#requestUpdateTransform();
    this.#isDragging = false;
  }

  /**
   * Process drag gesture from touch
   * @param {Touch} touch
   */
  #processDragGesture(touch) {
    // Check if we've moved beyond the drag threshold
    const distance = getDistance(touch, this.#startPosition);

    if (!this.#hasDraggedBeyondThreshold && distance < DRAG_THRESHOLD) {
      // Movement is too small, don't process as drag yet
      return;
    }

    this.#hasDraggedBeyondThreshold = true;

    // Calculate movement deltas for translation
    const dx = touch.clientX - this.#startPosition.x;
    const dy = touch.clientY - this.#startPosition.y;

    // Calculate new translation directly
    this.#translate.x = this.#startTranslate.x + dx / this.#scale;
    this.#translate.y = this.#startTranslate.y + dy / this.#scale;

    this.#requestUpdateTransform();
  }

  /**
   * AC-141 Figs zoom mode touchmove: prevents native handling only for the
   * gestures this element owns (pinch, zoomed drag).
   * @param {TouchEvent} event
   */
  #handleFigsTouchMove = (event) => {
    const touchCount = event.touches.length;

    if (touchCount === 2) {
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (touch1 && touch2) {
        preventDefault(event);
        this.#processZoomGesture(touch1, touch2);
      }
      return;
    }

    if (touchCount === 1 && this.#isDragging) {
      const touch = event.touches[0];
      if (touch) {
        preventDefault(event);
        this.#processDragGesture(touch);
      }
    }
  };

  /**
   * @param {TouchEvent} event
   */
  #handleTouchEnd = (event) => {
    if (event.touches.length === 0) {
      this.#isDragging = false;
      this.#requestUpdateTransform();

      this.#hasDraggedBeyondThreshold = false;
    }
  };

  /**
   * Constrain image translation to keep it within the viewport
   */
  #constrainTranslation() {
    const containerWidth = this.clientWidth;
    const containerHeight = this.clientHeight;
    if (!containerWidth || !containerHeight || !this.#image) return;

    // Keep scale between MIN_ZOOM (1) and the mode's max (5, or 2 in figs mode)
    this.#scale = clamp(this.#scale, MIN_ZOOM, this.#maxZoom);

    // At minimum zoom (1x), the full image should be visible with no dragging allowed
    if (this.#scale <= MIN_ZOOM) {
      this.#translate.x = 0;
      this.#translate.y = 0;
      return;
    }

    // Get wrapper dimensions
    const wrapperRect = this.getBoundingClientRect();

    // Calculate ACTUAL image content dimensions at current zoom
    // The image element may fill the wrapper, but the content has its own aspect ratio
    const imageElement = this.#image;
    let naturalWidth, naturalHeight;

    // Try to get natural dimensions
    if (imageElement.naturalWidth > 0 && imageElement.naturalHeight > 0) {
      naturalWidth = imageElement.naturalWidth;
      naturalHeight = imageElement.naturalHeight;
    } else {
      // Fallback: assume square image if we can't get natural dimensions
      naturalWidth = wrapperRect.width;
      naturalHeight = wrapperRect.width;
    }

    // Calculate how the image fits within the wrapper (object-fit: contain behavior)
    const imageAspectRatio = naturalWidth / naturalHeight;
    const wrapperAspectRatio = wrapperRect.width / wrapperRect.height;

    let actualImageWidth, actualImageHeight;

    if (imageAspectRatio > wrapperAspectRatio) {
      // Image is wider - width fits exactly, height is smaller (letterboxed top/bottom)
      actualImageWidth = wrapperRect.width;
      actualImageHeight = actualImageWidth / imageAspectRatio;
    } else {
      // Image is taller - height fits exactly, width is smaller (letterboxed left/right)
      actualImageHeight = wrapperRect.height;
      actualImageWidth = actualImageHeight * imageAspectRatio;
    }

    // Apply current zoom scale
    const scaledImageWidth = actualImageWidth * this.#scale;
    const scaledImageHeight = actualImageHeight * this.#scale;

    // SIMPLE APPROACH: Calculate constraints directly from image content dimensions
    // If image content is larger than wrapper, calculate max translation directly

    const horizontalOverflow = Math.max(0, scaledImageWidth - wrapperRect.width);
    const verticalOverflow = Math.max(0, scaledImageHeight - wrapperRect.height);

    // Max translation is half the overflow (since image starts centered)
    const maxTranslateX = horizontalOverflow / 2 / this.#scale;
    const maxTranslateY = verticalOverflow / 2 / this.#scale;

    // Apply symmetric constraints (object-fit: contain behavior)
    // Image starts centered, can move maxTranslate in each direction
    this.#translate.x = clamp(this.#translate.x, -maxTranslateX, maxTranslateX);
    this.#translate.y = clamp(this.#translate.y, -maxTranslateY, maxTranslateY);

    // Apply final transforms to CSS
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
  }

  /**
   * Request an animation frame to update the transform
   */
  #requestUpdateTransform = () => {
    if (!this.#animationFrame) {
      this.#animationFrame = requestAnimationFrame(this.#updateTransform);
    }
  };

  /**
   * Cancel any pending animation frame
   */
  #cancelAnimationFrame() {
    if (this.#animationFrame) {
      cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = null;
    }
  }

  #updateTransform = () => {
    this.#animationFrame = null;

    this.#constrainTranslation();
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);

    // AC-141 figs mode: expose zoomed state for CSS (cursor, touch-action).
    if (this.#isFigs) {
      this.toggleAttribute('data-figs-zoomed', this.#scale > MIN_ZOOM + 0.01);
    }
  };

  /**
   * AC-141 figs mode: mouse affordances — double-click toggles 1x/2x and,
   * while zoomed, dragging pans (Figs measures panOnMouseMove: false with
   * drag panning, FIGS-RUNTIME-ADDENDUM §6). Touch input is served by the
   * touch listeners; pointer handlers act on mouse pointers only.
   */
  #initFigsPointerListeners() {
    const { signal } = this.#controller;

    this.addEventListener('dblclick', this.#handleFigsDoubleClick, { signal });
    this.addEventListener('pointerdown', this.#handleFigsPointerDown, { signal });
    this.addEventListener('pointermove', this.#handleFigsPointerMove, { signal });
    this.addEventListener('pointerup', this.#handleFigsPointerEnd, { signal });
    this.addEventListener('pointercancel', this.#handleFigsPointerEnd, { signal });
    // Native image drag would hijack the pan gesture.
    this.addEventListener('dragstart', preventDefault, { signal });
    this.addEventListener('click', this.#handleFigsClickCapture, { signal, capture: true });
  }

  /**
   * @param {MouseEvent} event
   */
  #handleFigsDoubleClick = (event) => {
    event.preventDefault();
    this.#animateNextTransform();
    // MouseEvent carries clientX/clientY, which is all the toggle reads.
    this.#handleDoubleTapFromTouch(/** @type {Touch} */ (/** @type {unknown} */ (event)));
  };

  /**
   * @param {PointerEvent} event
   */
  #handleFigsPointerDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    if (this.#scale <= MIN_ZOOM) return;

    this.setPointerCapture(event.pointerId);
    this.#startDragGestureFromTouch(/** @type {Touch} */ (/** @type {unknown} */ (event)));
    this.toggleAttribute('data-figs-dragging', true);
  };

  /**
   * @param {PointerEvent} event
   */
  #handleFigsPointerMove = (event) => {
    if (event.pointerType !== 'mouse' || !this.#isDragging) return;

    this.#processDragGesture(/** @type {Touch} */ (/** @type {unknown} */ (event)));
  };

  /**
   * @param {PointerEvent} event
   */
  #handleFigsPointerEnd = (event) => {
    if (event.pointerType !== 'mouse') return;

    this.removeAttribute('data-figs-dragging');
    if (!this.#isDragging) return;

    this.#isDragging = false;
    if (this.#hasDraggedBeyondThreshold) this.#suppressNextClick = true;
    this.#hasDraggedBeyondThreshold = false;
    this.#requestUpdateTransform();
  };

  /**
   * @param {MouseEvent} event
   */
  #handleFigsClickCapture = (event) => {
    if (!this.#suppressNextClick) return;

    this.#suppressNextClick = false;
    event.preventDefault();
    event.stopPropagation();
  };

  /**
   * AC-141 figs mode: briefly animates the next transform change (the
   * double-click / double-tap toggle); disabled under prefers-reduced-motion.
   * The attribute drives a scoped transition rule in assets/pdp-gallery.css.
   */
  #animateNextTransform() {
    if (prefersReducedMotion()) return;

    this.setAttribute('data-figs-animating', '');
    window.setTimeout(() => this.removeAttribute('data-figs-animating'), 350);
  }

  /**
   * Reset zoom to the rest state (1.5x default / 1x figs, centered position)
   * Called when zoom is exited/closed
   */
  #resetZoom = () => {
    // Reset scale and translation to defaults
    this.#scale = this.#restZoom;
    this.#startScale = this.#restZoom;
    this.#translate.x = 0;
    this.#translate.y = 0;

    // Reset gesture state to prevent interference on next zoom open
    this.#startPosition = { x: 0, y: 0 };
    this.#startTranslate = { x: 0, y: 0 };
    this.#isDragging = false;
    this.#lastTapTime = 0;
    this.#lastTapPosition = null;
    this.#hasDraggedBeyondThreshold = false;

    // Update CSS properties to reflect reset state
    this.style.setProperty('--drag-zoom-scale', this.#restZoom.toString());
    this.style.setProperty('--drag-zoom-translate-x', '0px');
    this.style.setProperty('--drag-zoom-translate-y', '0px');

    // AC-141 figs mode: clear the zoomed-state marker on reset.
    if (this.#isFigs) {
      this.removeAttribute('data-figs-zoomed');
      this.#hasManualZoom = false;
    }
  };

  destroy() {
    this.#controller.abort();
    this.#cancelAnimationFrame();
  }
}

/**
 * Calculate distance between two points or touches
 * @param {Point | Touch} point1 - First point or touch
 * @param {Point | Touch} point2 - Second point or touch
 * @returns {number} Distance between the points
 */
function getDistance(point1, point2) {
  // Handle both Point objects (x, y) and Touch objects (clientX, clientY)
  const x1 = /** @type {Point} */ (point1).x ?? /** @type {Touch} */ (point1).clientX;
  const y1 = /** @type {Point} */ (point1).y ?? /** @type {Touch} */ (point1).clientY;
  const x2 = /** @type {Point} */ (point2).x ?? /** @type {Touch} */ (point2).clientX;
  const y2 = /** @type {Point} */ (point2).y ?? /** @type {Touch} */ (point2).clientY;

  const dx = x1 - x2;
  const dy = y1 - y2;
  return Math.sqrt(dx * dx + dy * dy);
}

if (!customElements.get('pdp-drag-zoom-wrapper')) {
  customElements.define('pdp-drag-zoom-wrapper', DragZoomWrapper);
}

/**
 * @typedef {Object} Point
 * @property {number} x
 * @property {number} y
 */

/**
 * @typedef {HTMLElement} ZoomDialogElement
 * @property {Function} close - Method to close the zoom dialog
 */
